import React, { useMemo, useState, useEffect } from 'react';
import { useApplicationStore } from '../../stores/applicationStore';
import { ApplicationStatus, JobApplication } from '../../types';
import { ApplicationDetail } from './ApplicationDetail';
import { Plus, Building2, BadgeCheck, Clock, XCircle, CheckCircle, AlertCircle, Eye, Edit, Trash2, Search, Calendar, Mail, Paperclip, Filter, X, ArrowLeft, RefreshCw, FileText, TrendingUp, Target } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const statusOptions: ApplicationStatus[] = [
  'In attesa',
  'Colloquio programmato',
  'Accettato',
  'Rifiutato',
  'Scaduto',
];

const statusBadge = {
  'In attesa': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: <Clock className="w-3 h-3" /> },
  'Colloquio programmato': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: <BadgeCheck className="w-3 h-3" /> },
  'Accettato': { color: 'bg-green-100 text-green-800 border-green-200', icon: <CheckCircle className="w-3 h-3" /> },
  'Rifiutato': { color: 'bg-red-100 text-red-800 border-red-200', icon: <XCircle className="w-3 h-3" /> },
  'Scaduto': { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: <AlertCircle className="w-3 h-3" /> },
};

const PAGE_SIZE = 10;

export const ApplicationList: React.FC = () => {
  const { 
    applications, 
    loading, 
    initialized,
    loadApplications,
    deleteApplicationFromFirestore 
  } = useApplicationStore();
  const { user, userProfile } = useAuthStore();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Carica le candidature quando l'utente è autenticato
  useEffect(() => {
    if (user?.uid && !initialized && !loading) {
      console.log('🔄 Caricamento candidature per utente:', user.uid);
      loadApplications(user.uid);
    }
  }, [user?.uid, initialized, loading, loadApplications]);

  // Leggi il parametro status dall'URL e applica il filtro
  useEffect(() => {
    const statusFromUrl = searchParams.get('status');
    if (statusFromUrl && statusFromUrl !== statusFilter) {
      console.log('🔍 Applicazione filtro da URL:', statusFromUrl);
      setStatusFilter(statusFromUrl);
      setPage(1); // Reset alla prima pagina
    }
  }, [searchParams, statusFilter]);

  // Filtra e cerca
  const filtered = useMemo(() => {
    let list = applications;
    if (statusFilter) list = list.filter((a: JobApplication) => a.status === statusFilter);
    if (search) list = list.filter((a: JobApplication) =>
      a.companyName.toLowerCase().includes(search.toLowerCase()) ||
      a.position.toLowerCase().includes(search.toLowerCase())
    );
    return list;
  }, [applications, statusFilter, search]);

  // Paginazione
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Statistiche
  const stats = {
    total: applications.length,
    inAttesa: applications.filter(a => a.status === 'In attesa').length,
    colloqui: applications.filter(a => a.status === 'Colloquio programmato').length,
    accettate: applications.filter(a => a.status === 'Accettato').length,
  };

  const handleDelete = async (applicationId: string) => {
    if (!user?.uid) return;
    
    // Conferma eliminazione
    const isConfirmed = window.confirm('Sei sicuro di eliminare questa candidatura? Questa azione non può essere annullata.');
    if (!isConfirmed) return;
    
    try {
      await deleteApplicationFromFirestore(user.uid, applicationId);
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
    }
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setPage(1);
  };

  const handleRefresh = async () => {
    if (!user?.uid) return;
    setIsRefreshing(true);
    try {
      await loadApplications(user.uid);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (loading && !initialized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Caricamento candidature...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-2 lg:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 lg:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="p-1.5 lg:p-2 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-lg lg:rounded-xl transition-colors"
            >
              <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5" />
            </button>
            <div>
              <h1 className="text-lg lg:text-2xl xl:text-3xl font-bold text-blue-900">Le Mie Candidature</h1>
              <p className="text-sm lg:text-base text-gray-600 mt-0.5 lg:mt-1">Gestisci tutte le tue candidature di lavoro</p>
            </div>
          </div>
          <div className="flex items-center gap-2 lg:gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-1.5 lg:p-2 bg-gray-100 hover:bg-blue-100 text-blue-700 rounded-lg lg:rounded-xl transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 lg:w-5 lg:h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate('/applications/new')}
              className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 xl:px-6 py-1.5 lg:py-2 xl:py-3 bg-blue-600 text-white rounded-lg lg:rounded-xl shadow-sm hover:bg-blue-700 transition-colors font-medium text-sm lg:text-base"
            >
              <Plus className="h-4 w-4" />
              <span>Nuova Candidatura</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistiche - Stile Calendar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 lg:p-4">
        <div className="grid grid-cols-4 gap-2 lg:gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 text-center">
            <div className="text-blue-600 text-lg font-bold">{stats.total}</div>
            <div className="text-xs text-blue-700 font-medium">Totale</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-3 text-center">
            <div className="text-yellow-600 text-lg font-bold">{stats.inAttesa}</div>
            <div className="text-xs text-yellow-700 font-medium">In Attesa</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 text-center">
            <div className="text-blue-600 text-lg font-bold">{stats.colloqui}</div>
            <div className="text-xs text-blue-700 font-medium">Colloqui</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 text-center">
            <div className="text-green-600 text-lg font-bold">{stats.accettate}</div>
            <div className="text-xs text-green-700 font-medium">Accettate</div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 lg:p-6">
        <div className="flex flex-col lg:flex-row gap-2 lg:gap-4">
          {/* Search Bar */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 lg:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 lg:h-5 lg:w-5" />
            <input
              type="text"
              placeholder="Cerca azienda o posizione..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 lg:pl-12 pr-10 lg:pr-12 py-2 lg:py-3 border border-gray-200 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 lg:right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <div className="lg:w-64">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 lg:px-4 py-2 lg:py-3 border border-gray-200 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
            >
              <option value="">Tutti gli stati</option>
              {statusOptions.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </div>

          {/* Clear Filters */}
          {(search || statusFilter) && (
            <button
              onClick={clearFilters}
              className="px-3 lg:px-4 py-2 lg:py-3 text-blue-600 hover:text-blue-700 font-medium text-sm"
            >
              Reset Filtri
            </button>
          )}
        </div>

        {/* Active Filters */}
        {(search || statusFilter) && (
          <div className="flex flex-wrap gap-2 mt-2 lg:mt-4">
            {search && (
              <span className="inline-flex items-center gap-1 px-2 lg:px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                "{search}"
                <button onClick={() => setSearch('')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
            {statusFilter && (
              <span className="inline-flex items-center gap-1 px-2 lg:px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                {statusFilter}
                <button onClick={() => setStatusFilter('')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Lista Candidature */}
      {paginated.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 lg:p-8 text-center">
          <Building2 className="h-12 w-12 lg:h-16 lg:w-16 text-gray-300 mx-auto mb-3 lg:mb-4" />
          <h3 className="text-base lg:text-lg font-semibold text-gray-900 mb-2">
            {search || statusFilter ? 'Nessuna candidatura trovata' : 'Nessuna candidatura'}
          </h3>
          <p className="text-gray-600 mb-4 lg:mb-6 text-sm">
            {search || statusFilter 
              ? 'Prova a modificare i filtri di ricerca'
              : 'Inizia aggiungendo la tua prima candidatura'
            }
          </p>
          {!search && !statusFilter && (
            <button
              onClick={() => navigate('/applications/new')}
              className="inline-flex items-center gap-2 px-4 lg:px-6 py-2 lg:py-3 bg-blue-600 text-white rounded-lg lg:rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              <span>Prima Candidatura</span>
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-6">
          {paginated.map((application) => {
            // Calcola i giorni trascorsi dalla candidatura
            const daysSinceApplied = Math.floor((new Date().getTime() - application.appliedDate.toDate().getTime()) / (1000 * 60 * 60 * 24));
            const isRecent = daysSinceApplied <= 7;
            const isOld = daysSinceApplied > 30;
            
            return (
              <div
                key={application.id}
                className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl hover:scale-[1.02] transition-all duration-300 h-full flex flex-col group"
              >
                {/* Header Card con gradient */}
                <div className="p-4 lg:p-6 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex-1 min-w-0">
                      <h3 
                        className="text-lg lg:text-xl font-bold text-gray-900 group-hover:text-blue-600 cursor-pointer transition-colors truncate mb-2"
                        onClick={() => navigate(`/applications/${application.id}`)}
                      >
                        {application.companyName}
                      </h3>
                      <p className="text-gray-700 text-sm lg:text-base font-semibold mb-3">
                        {application.position}
                      </p>
                    </div>
                    
                    {/* Action Buttons con gradienti */}
                    <div className="flex items-center gap-1.5 relative z-10">
                      <button
                        onClick={() => navigate(`/applications/${application.id}`)}
                        className="p-2 lg:p-2.5 bg-gradient-to-br from-blue-50 to-blue-100 text-blue-600 hover:from-blue-100 hover:to-blue-200 rounded-xl transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md"
                        title="Visualizza dettagli"
                      >
                        <Eye className="h-4 w-4 lg:h-5 lg:w-5" />
                      </button>
                      <button
                        onClick={() => navigate(`/applications/${application.id}/edit`)}
                        className="p-2 lg:p-2.5 bg-gradient-to-br from-green-50 to-green-100 text-green-600 hover:from-green-100 hover:to-green-200 rounded-xl transition-all duration-200 cursor-pointer shadow-sm hover:shadow-md"
                        title="Modifica"
                      >
                        <Edit className="h-4 w-4 lg:h-5 lg:w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(application.id)}
                        className="p-2 lg:p-2.5 bg-gradient-to-br from-red-50 to-red-100 text-red-600 hover:from-red-100 hover:to-red-200 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
                        title="Elimina"
                      >
                        <Trash2 className="h-4 w-4 lg:h-5 lg:w-5" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Status Badge con gradienti */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs lg:text-sm font-semibold shadow-sm ${
                      application.status === 'In attesa' 
                        ? 'bg-gradient-to-r from-yellow-100 to-yellow-200 text-yellow-800 border border-yellow-300' 
                        : application.status === 'Colloquio programmato'
                        ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 border border-blue-300'
                        : application.status === 'Accettato'
                        ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800 border border-green-300'
                        : application.status === 'Rifiutato'
                        ? 'bg-gradient-to-r from-red-100 to-red-200 text-red-800 border border-red-300'
                        : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-800 border border-gray-300'
                    }`}>
                      {statusBadge[application.status].icon}
                      <span className="hidden lg:inline">{application.status}</span>
                      <span className="lg:hidden">{application.status === 'In attesa' ? 'In Attesa' : application.status === 'Colloquio programmato' ? 'Colloquio' : application.status}</span>
                    </span>
                    
                    {/* Indicatore di priorità basato sulla data */}
                    {isRecent && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-green-100 to-green-200 text-green-700 rounded-full text-xs font-medium border border-green-300">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                        Recente
                      </span>
                    )}
                    {isOld && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-orange-100 to-orange-200 text-orange-700 rounded-full text-xs font-medium border border-orange-300">
                        <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
                        Vecchia
                      </span>
                    )}
                  </div>

                  {/* Details Section migliorata */}
                  <div className="mt-auto space-y-3">
                    {/* Informazioni principali */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-2">
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <Calendar className="h-3 w-3 text-gray-500" />
                          <span className="text-xs font-medium text-gray-500">Data Invio:</span>
                        </div>
                        <span className="text-sm font-bold text-gray-900">
                          {format(application.appliedDate.toDate(), 'dd/MM/yyyy', { locale: it })}
                        </span>
                      </div>
                      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-2">
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <Mail className="h-3 w-3 text-gray-500" />
                          <span className="text-xs font-medium text-gray-500">Contatto:</span>
                        </div>
                        <span className="text-sm font-bold text-gray-900 capitalize">
                          {application.contactMethod}
                        </span>
                      </div>
                    </div>

                    {/* Allegati con icona migliorata */}
                    {application.attachments.length > 0 && (
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-2">
                        <div className="flex items-center gap-2">
                          <Paperclip className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-semibold text-blue-800">
                            {application.attachments.length} allegato{application.attachments.length !== 1 ? 'i' : ''}
                          </span>
                        </div>
                      </div>
                    )}

                    {/* Note con preview migliorata */}
                    {application.notes && (
                      <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-3 border-l-4 border-purple-300">
                        <div className="flex items-center gap-2 mb-2">
                          <FileText className="h-4 w-4 text-purple-600" />
                          <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Note</span>
                        </div>
                        <p className="text-sm text-purple-800 line-clamp-2 leading-relaxed">
                          {application.notes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Paginazione */}
      {totalPages > 1 && (
        <div className="flex justify-center">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 lg:p-4">
            <div className="flex items-center gap-2 lg:gap-3">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 lg:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                Precedente
              </button>
              <span className="px-3 lg:px-4 py-2 text-gray-700 text-sm font-medium">
                {page} di {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="px-3 lg:px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                Successiva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 